import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { asyncScheduler, firstValueFrom, interval, Observable, of, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  distinctUntilKeyChanged,
  endWith,
  first,
  map,
  skipWhile,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { Counters, SendSmsPhone } from './../../store/actions/authentication.actions';
import { AppState } from './../../store/app-reducers';
import { IParamsCreateCode, IResultCounters } from './../models/ApplicationUser';
import { KEYS_COUNTERS, LEVEL_COUNTER_SMS } from './../models/Constants';

@Injectable({
  providedIn: 'root'
})
export class SmsServiceService {

  constructor(
    private store: Store<AppState>
    , private translate: TranslateService
  ) { }

  public temporizador$ = () => {
    const keys$ = this.translate.get('Miscellaneous.keys-counters.sms').pipe(catchError(() => of(KEYS_COUNTERS.sms)));
    const levelTime$: Observable<{ [key: string]: number }> = this.translate.get('Miscellaneous.level-counter-sms').pipe(catchError(() => of(LEVEL_COUNTER_SMS)));
    const counters$ = this.store.select('Counters').pipe(
      skipWhile(x => !x.success && !x.failed),
      map(value => value.data),
      concatMap(counters => keys$.pipe(map((llave: string) => ({ llave, counters })))),
      map(({ llave, counters }) => counters.filter(c => c.Llave === llave)[0]),
      skipWhile(counters => (counters.CantidadRegistros || 0) < 1),
      distinctUntilKeyChanged('UltimoRegistro'),
      concatMap(counters => levelTime$.pipe(map(level => ({ ...counters, time: level[(counters.CantidadRegistros || 0)] })))),
    );
    const interval$ = interval(1000).pipe(startWith(0));
    let timeEnd$ = new Observable();

    return counters$.pipe(
      tap(counter => {
        const remaining = this.restanteSegundos(counter.UltimoRegistro || '', counter.time);
        timeEnd$ = remaining < 0 ? new Observable() : timer(this.finalTime(counter.UltimoRegistro || '', counter.time).toDate())
          .pipe(first(), tap(() => asyncScheduler.schedule(() => this.store.dispatch(Counters()), 1000)));
      }),
      switchMap(counter => interval$.pipe(
        map(() => this.restanteSegundos(counter.UltimoRegistro || '', counter.time)),
        skipWhile(countdown => countdown < 0),
        map(countdown => ({ countdown, time: this.formatTime(countdown) })),
        takeUntil(timeEnd$),
        endWith({ countdown: 0, time: '00s' }),
      )),
    )
  }

  private finalTime = (UltimoRegistro: string, time: number) => moment(UltimoRegistro).utc(true).add(time, 'minutes');

  private restanteSegundos = (UltimoRegistro: string, time: number): number => {
    const finalTime = this.finalTime(UltimoRegistro, time);
    const now = moment().utc(false);

    return finalTime.diff(now, 'seconds');
  }

  private formatTime = (time: number): string => {
    const days = Math.floor(time / (60 * 60 * 24));
    const hours = Math.floor((time % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((time % (60 * 60)) / 60);
    const seconds = time % 60;

    const pad = (num: number) => num.toString().padStart(2, '0');

    return (days > 0 ? `${days}d ` : '') +
      (hours > 0 ? `${pad(hours)}:` : '') +
      (minutes > 0 ? `${pad(minutes)}:` : '') +
      `${pad(seconds)}` +
      (hours > 0 ? 'hr' : minutes > 0 ? 'min' : 's');
  }

  public sendSmsPhone = async (params: IParamsCreateCode) => {
    const counter$ = this.store.select('Counters')
      .pipe(
        skipWhile(x => !x.success && !x.failed),
        map(value => value.data),
        switchMap(counters => this.translate.get('Miscellaneous.keys-counters.sms').pipe(map((llave: string) => ({ llave, counters })))),
        map(({ llave, counters }) => counters.filter(c => c.Llave === llave)[0]),
        first(),
      );
    const levelTime$ = this.translate.get('Miscellaneous.level-counter-sms')
      .pipe(
        concatMap<{ [key: string]: number }, Observable<number>>(level => counter$.pipe(map<IResultCounters, number>(counter => level[(counter.CantidadRegistros || 0) + 1]))),
        catchError(() => of<number>(LEVEL_COUNTER_SMS[2])),
        first(),
      );
    const DuracionCodigoMin = await firstValueFrom(levelTime$);
    this.store.dispatch(SendSmsPhone({ ...params, DuracionCodigoMin }));
  }

}
